// filterFlightsByAirports — pure filtering of API flights by origin/destination.
//
// Extracted verbatim from `FlightLookup.js` (module-level `filterFlightsByAirports`)
// as part of the behavior-preserving refactor (issue #240). When neither airport
// is requested it returns the data unchanged. Otherwise it filters by exact
// origin+destination, then falls back to origin-only matching for connecting
// itineraries, and finally throws a descriptive Error when nothing matches.
//
// IMPORTANT — totality is mirrored exactly, including the THROW: the source threw
// an `Error` on no-match (caught by the container's try/catch and surfaced to the
// user), so this function intentionally throws the same error rather than
// returning a fallback. No new guards are added around `data.flights`; the source
// dereferenced it directly.
//
// React-free and side-effect-free (aside from the source's intentional throw).
//
// Validates: Requirements 3.1, 3.4, 3.5, 1.6

import toIATA from './toIATA.js';
import formatAvailableLegs from './formatAvailableLegs.js';

/**
 * Filters flights by departure/arrival airports, with an origin-only fallback.
 *
 * @param {Object} data - API response carrying a `flights` array.
 * @param {string} wantOrigin - Desired origin IATA (uppercased), or falsy for any.
 * @param {string} wantDest - Desired destination IATA (uppercased), or falsy for any.
 * @param {string} airline - Airline code, used only in the no-match error message.
 * @param {string} flightNumber - Flight number, used only in the no-match error message.
 * @returns {Object} The data object with its `flights` filtered.
 * @throws {Error} When no flight matches the requested airports.
 */
export function filterFlightsByAirports(data, wantOrigin, wantDest, airline, flightNumber) {
  if (!wantOrigin && !wantDest) {
    return data;
  }

  const filteredFlights = data.flights.filter(function(f) {
    var orig = toIATA(f.origin);
    var dest = toIATA(f.destination);
    return (!wantOrigin || orig === wantOrigin) && (!wantDest || dest === wantDest);
  });

  if (filteredFlights.length > 0) {
    return { ...data, flights: filteredFlights };
  }

  // No exact match — try origin-only fallback for connecting itineraries
  if (wantOrigin && wantDest) {
    var originOnlyFlights = data.flights.filter(function(f) {
      return toIATA(f.origin) === wantOrigin;
    });
    if (originOnlyFlights.length > 0) {
      return { ...data, flights: originOnlyFlights };
    }
  }

  // No match at all — throw descriptive error
  var legs = formatAvailableLegs(data.flights);
  throw new Error(
    'No ' + airline + flightNumber + ' found departing ' +
    (wantOrigin || 'unknown') + ' on this date. Available legs: ' + legs
  );
}

export default filterFlightsByAirports;
